import React, { createContext, useContext, useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '../store/authStore';
import { SERVER_URL } from '../config/api';

interface ProgressUpdate {
  sessionId: string;
  userId: number;
  stage: string;
  progress: number;
  message: string;
  currentSection?: number;
  totalSections?: number;
  timestamp: string;
}

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  onProgressUpdate: (callback: (update: ProgressUpdate) => void) => () => void;
  onAnalysisCompleted: (callback: (data: { sessionId: string; analysisId: number }) => void) => () => void;
  onAnalysisError: (callback: (data: { sessionId: string; error: string }) => void) => () => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = useAuthStore((state) => state.token);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!token) {
      // Disconnect if no token
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    // Connect to Socket.IO server
    const newSocket = io(SERVER_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
    });

    newSocket.on('connect', () => {
      console.log('✅ WebSocket connected');
      setIsConnected(true);
    });

    newSocket.on('disconnect', () => {
      console.log('❌ WebSocket disconnected');
      setIsConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
      setIsConnected(false);
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [token]);

  const onProgressUpdate = (callback: (update: ProgressUpdate) => void) => {
    if (!socket) return () => {};

    socket.on('analysis-progress', callback);

    return () => {
      socket.off('analysis-progress', callback);
    };
  };

  const onAnalysisCompleted = (callback: (data: { sessionId: string; analysisId: number }) => void) => {
    if (!socket) return () => {};

    socket.on('analysis-completed', callback);

    return () => {
      socket.off('analysis-completed', callback);
    };
  };

  const onAnalysisError = (callback: (data: { sessionId: string; error: string }) => void) => {
    if (!socket) return () => {};

    socket.on('analysis-error', callback);

    return () => {
      socket.off('analysis-error', callback);
    };
  };

  return (
    <SocketContext.Provider value={{ socket, isConnected, onProgressUpdate, onAnalysisCompleted, onAnalysisError }}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};
